import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('connectmail_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/users/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth init failed:', error);
          localStorage.removeItem('connectmail_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken } = response.data;
    localStorage.setItem('connectmail_token', newToken);
    setToken(newToken);
    // Fetch full profile immediately so user object has all fields including images
    try {
      const profileRes = await axios.get(`${API}/users/profile`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(profileRes.data);
    } catch {
      setUser(response.data.user);
    }
    return response.data.user;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken } = response.data;
    localStorage.setItem('connectmail_token', newToken);
    setToken(newToken);
    // Fetch full profile immediately
    try {
      const profileRes = await axios.get(`${API}/users/profile`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(profileRes.data);
    } catch {
      setUser(response.data.user);
    }
    return response.data.user;
  };

  const logout = () => {
    localStorage.removeItem('connectmail_token');
    setToken(null);
    setUser(null);
  };

  const refreshProfile = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/users/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Profile refresh failed:', error);
      }
    }
  }, [token]);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshProfile,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
